import React, { useMemo, useState, useEffect, useCallback, useRef } from "react"
import { debounce } from "lodash"
import styles from "./flowautocomplete.module.css"
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import { useTrigger } from "../../stores/store"
import { Triggers, useSelector } from "../../stores/dashboard.store"
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ColorChip from "../color-chip"
import NodeInfo from "../node-info/node-info"
import Tooltip from "../tooltip"

const getClipColor = (clip) => {
	switch (clip) {
		case "free":
			return [64, 190, 81]
		case "pro":
			return [255, 191, 78]
		case "experimental":
			return [255, 78, 78]
		case "standard":
			return [78, 120, 255]
		default:
			return "unset"
	}
}

const ItemDocArea = ({ rest, expanded }) => {
	return (
		<div
			className={styles.itemMoreInfo}
			style={{
				maxHeight: expanded ? "500px" : "0px",
				overflow: "hidden",
				transition: "max-height 0.3s ease-in-out",
				padding: expanded ? "15px 25px" : "0px 25px", // Ensure horizontal padding remains
				opacity: expanded ? 1 : 0, // Smooth fade
				transition: "max-height 0.3s ease-in-out, padding 0.3s ease-in-out, opacity 0.2s ease-in-out",
			}}
		>
			<NodeInfo data={rest} />
		</div>
	)
}

const Item = ({ plan, localOnSelect, id, name, description, accessibility, ...rest }) => {
	const [expanded, setExpanded] = useState(false)

	const toggleExpand = () => setExpanded((prev) => !prev)

	return (
		<div
			className={styles.item}
			key={id}
			style={{
				borderLeft: `3px solid rgb(${Array.isArray(rest?.color) ? rest.color.join(",") : "200,200,200"})`,
			}}
		>
			<div className={styles.itemContainer}>
				<div
					className={styles.itemContent}
					onClick={() => localOnSelect({ ...rest, name, id, accessibility, description })}
				>
					<div style={{ flex: 1 }}>
						<div className={styles.itemTitle}>{name}</div>
						{description && <div className={styles.itemDescription}>{description}</div>}
					</div>
					{accessibility !== undefined &&
						plan?.nodeAccessibility.find((x) => x === accessibility) === undefined &&
						(
							<Tooltip
								title="This node is not available in your current plan."
							>
								<ColorChip label={accessibility} color={getClipColor(accessibility)} />
							</Tooltip>
						)}
				</div>

				{rest?.docs !== undefined && (
					<div style={{ borderLeft: "1px solid var(--border-secondary)" }}>
						<div
							style={{
								color: "var(--text-primary)",
								padding: 15,
								cursor: "pointer",
								transition: "transform 0.3s ease-in-out",
								transform: expanded ? "rotate(0deg)" : "rotate(-180deg)",
							}}
							onClick={toggleExpand}
						>
							<KeyboardArrowUpIcon style={{ fontSize: 25 }} />
						</div>
					</div>
				)}
			</div>
			{rest?.docs !== undefined && <ItemDocArea rest={rest} expanded={expanded} />}
		</div>
	)
}

const doesInputIncludeType = (type, isInput, entry, isDragged = false) => {	
	const areAllInputEdges = entry?.inputs?.every(x => x?.use_as_edge === false)
	
	if(!isInput&&areAllInputEdges) {
		return false
	}

	if (isInput) return !!entry?.outputs?.find(x => x?.type === type)
	return !!entry?.inputs?.find(x => x?.type === type)
}

const AutoComplete = ({ onSelect, items = [], disableCollapse = true, onClose, selectionInfo }) => {
	const [text, setText] = useState("")
	const [debouncedText, setDebouncedText] = useState(text)
	const [collapsedCategories, setCollapsedCategories] = useState({})

	const { plan } = useSelector()

	const reactFlowViewPort = useTrigger(Triggers.GetReactFlowViewport)

	const inputRef = useRef(null)

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus()
		}
	}, [])

	const updateDebouncedText = useCallback(
		debounce((value) => setDebouncedText(value), 100),
		[]
	)
	useEffect(() => {
		updateDebouncedText(text)
	}, [text, updateDebouncedText])

	const filteredItems = useMemo(
		() => {
			const res = debouncedText.length > 0
				? items.filter((x) =>
					x?.name?.toLowerCase().includes(debouncedText.toLowerCase()) ||
					x?.description?.toLowerCase().includes(debouncedText.toLowerCase()) ||
					x?.category?.toLowerCase().includes(debouncedText.toLowerCase()) ||
					x?.accessibility?.toLowerCase().includes(debouncedText.toLowerCase())
				)
				: items

			return !!selectionInfo?.type ? res.filter(x => doesInputIncludeType(selectionInfo?.type, selectionInfo?.isInput, x, selectionInfo?.isDragged)) : res
		},
		[debouncedText, items]
	)

	const categorizedItems = useMemo(() => {
		const filtered = filteredItems.reduce((acc, item) => {
			const category = item.category || "Project Nodes"
			if (!acc[category]) {
				acc[category] = []
			}
			acc[category].push(item)
			return acc
		}, {})

		return Object.keys(filtered).sort().reduce((a, c) => {
			a[c] = filtered[c].sort((a, b) => {
				const color = !Array.isArray(b?.color) ? [255, 255, 255] : b?.color
				const colorA = !Array.isArray(a?.color) ? [255, 255, 255] : a.color

				return (color[0] + color[1] + color[2]) - (colorA[0] + colorA[1] + colorA[2])
			})

			return a
		}, {})
	}, [filteredItems])

	const localOnSelect = (val) => {
		const bounds = reactFlowViewPort()
		onSelect({ ...val, flow: bounds })
	}

	const toggleCategory = (category) => {
		if (disableCollapse) return

		setCollapsedCategories((prev) => ({
			...prev,
			[category]: !prev[category],
		}))
	}

	return (
		<div id={"OutsideDialogContainer"} className={styles.fullscreenContainer} onClick={onClose}>
			<div id={"InsideDialogContainer"} className={styles.autocomplete} onClick={(e) => e.stopPropagation()}>
				<div className={styles.inputSection}>
					<SearchIcon style={{ paddingTop: '15px', paddingLeft: '15px', color: 'var(--text-primary)' }} />
					<input
						ref={inputRef}
						style={{ height: 'inherit', width: '100%', paddingLeft: '15px', backgroundColor: 'unset', color: 'var(--text-primary)', fontSize: '20px', paddingTop: '15px' }}
						type="text"
						value={text}
						placeholder="Search..."
						onChange={({ target: { value } }) => setText(value)}
					/>
				</div>
				<div className={styles.autocompleteBody}>
					<div className={styles.menuItems}>
						{Object.entries(categorizedItems).map(([category, items]) => (
							<div key={category} className={styles.categoryContainer}>
								<div
									className={styles.categoryHeader}
									onClick={() => toggleCategory(category)}
									style={{ display: 'flex', alignItems: 'center', cursor: disableCollapse ? 'default' : 'pointer' }}
								>
									<div>{category} </div>
									{!disableCollapse && (
										<>
											{collapsedCategories[category] === false ?
												<ExpandMoreIcon style={{ color: 'var(--text-secondary)' }} /> :
												<ExpandLessIcon style={{ color: 'var(--text-secondary)' }} />}
										</>
									)}
								</div>
								{(collapsedCategories[category] === undefined || collapsedCategories[category] === false) &&
									items.map((item) => (
										<Item plan={plan} localOnSelect={localOnSelect} {...item} />
									))}
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

export default AutoComplete